<template>
  <div class="container py-3">
    <!-- 提醒任務已完成訊息 -->
    <AlertMessage v-if="detailData && detailData.IsComplete" variant="primary">
      <h4 class="mb-0 text-primary text-spacing-5 py-1">
        <Icon
          :icon="CompleteChecked"
          width="1.2em"
          height="1.2em"
          class="me-2" />
        <span class="mb-0 align-middle fw-bolder">服務已完成</span>
      </h4>
    </AlertMessage>

    <!-- 提醒任務已取消訊息 -->
    <AlertMessage v-if="detailData && detailData.IsCancel" variant="secondary">
      <h4 class="mb-0 text-secondary text-spacing-5 py-1">
        <Icon
          :icon="CompleteChecked"
          width="1.2em"
          height="1.2em"
          class="me-2" />
        <span class="mb-0 align-middle fw-bolder">服務已取消</span>
      </h4>
    </AlertMessage>

    <!-- 任務需求 -->
    <div class="card border-0 rounded-lg shadow-sm mb-4">
      <!-- 標題 -->
      <div
        class="card-header border-0 bg-primary-light text-center rounded-top-lg">
        <span class="text-spacing-5 text-primary fw-bolder">任務需求</span>
      </div>
      <!-- 明細內容 -->
      <div class="card-body">
        <div v-if="detailData">
          <div class="pb-2 border-bottom border-bottom-dashed">
            <div class="row gx-2 align-items-center">
              <div class="col-auto">
                <Icon
                  :icon="Time"
                  width="1.2em"
                  height="1.2em"
                  class="text-primary" />
              </div>
              <div class="col-auto">
                <span class="small text-spacing-4 fw-bolder text-label-color"
                  >預約時間</span
                >
              </div>
              <div class="col">
                <span class="fs-4 fw-bolder text-primary">{{
                  OrderTimeFormat(detailData.OrderTime)
                }}</span>
              </div>
            </div>
          </div>
          <div class="py-2 border-bottom border-bottom-dashed">
            <div class="row gx-2 align-items-center">
              <div class="col-auto">
                <Icon
                  :icon="BoyFace"
                  width="1.2em"
                  height="1.2em"
                  class="text-primary" />
              </div>
              <div class="col-auto">
                <span class="small text-spacing-4 fw-bolder text-label-color"
                  >乘客姓名</span
                >
              </div>
              <div class="col">
                <span class="fs-4 fw-bolder text-primary">{{
                  detailData.Passenger?.Name
                }}</span>
              </div>
            </div>
          </div>
          <div class="py-2 border-bottom border-bottom-dashed">
            <div class="row gx-2 align-items-center">
              <div class="col-auto">
                <Icon
                  :icon="PhoneRounded"
                  width="1.2em"
                  height="1.2em"
                  class="text-primary" />
              </div>
              <div class="col-auto">
                <span class="small text-spacing-4 fw-bolder text-label-color"
                  >聯絡電話</span
                >
              </div>
              <div class="col">
                <span class="fs-4 fw-bolder text-primary">{{
                  detailData.Passenger?.PhoneNumber
                }}</span>
              </div>
            </div>
          </div>
          <div class="py-2">
            <div class="row gx-2 align-items-stretch">
              <!-- 左側上下車對應 -->
              <div class="col-auto py-2">
                <div class="row g-1 flex-column justify-content-center h-100">
                  <div class="col-auto" style="line-height: 1em">
                    <Icon
                      :icon="Circle"
                      width="1.2em"
                      height="1.2em"
                      class="text-primary" />
                  </div>
                  <div class="col">
                    <div
                      class="bg-primary h-100 mx-auto"
                      style="width: 3px; border-radius: 1px"></div>
                  </div>
                  <div class="col" style="line-height: 1em">
                    <Icon
                      :icon="LocationFilled"
                      width="1.2em"
                      height="1.2em"
                      class="text-primary" />
                  </div>
                </div>
              </div>

              <div class="col">
                <div class="py-2 border-bottom border-bottom-dashed">
                  <div class="small text-spacing-4 fw-bolder text-label-color">
                    搭車地點 | 上車地點備註
                  </div>
                  <p class="mb-0 fs-5">{{ detailData.StartAddress }}</p>
                  <div
                    v-if="detailData.AdditionMessage"
                    class="rounded bg-primary-light px-2 py-1">
                    {{ detailData.AdditionMessage }}
                  </div>
                </div>

                <div class="py-2">
                  <div class="small text-spacing-4 fw-bolder text-label-color">
                    搭車地點 | 上車地點備註
                  </div>
                  <p class="mb-0 fs-5">{{ detailData.TargetAddress }}</p>
                  <div
                    v-if="detailData.TargetAdditionMessage"
                    class="rounded bg-primary-light px-2 py-1">
                    {{ detailData.TargetAdditionMessage }}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- 服務紀錄 -->
    <div class="card border-0 rounded-lg shadow-sm mb-4">
      <!-- 標題 -->
      <div
        class="card-header border-0 bg-primary-light text-center rounded-top-lg">
        <span class="text-spacing-5 text-primary fw-bolder">服務紀錄</span>
      </div>
      <!-- 明細內容 -->
      <div class="card-body">
        <div v-if="detailData">
          <div class="row g-0 border-bottom border-bottom-dashed">
            <!-- 任務流程紀錄 -->
            <div class="col">
              <div class="pb-1 border-bottom border-bottom-dashed">
                <div class="row gx-2 align-items-center">
                  <div class="col">
                    <span
                      class="small text-spacing-4 fw-bolder text-label-color"
                      >到達時間</span
                    >
                  </div>
                  <div class="col-auto">
                    <span
                      class="fs-3 text-primary"
                      :title="detailData.ArrivalTime?.toString()">
                      {{
                        ValueOrDash(ProcessTimeFormat(detailData.ArrivalTime))
                      }}
                    </span>
                  </div>
                </div>
              </div>
              <div class="py-1 border-bottom border-bottom-dashed">
                <div class="row gx-2 align-items-center">
                  <div class="col">
                    <span
                      class="small text-spacing-4 fw-bolder text-label-color"
                      >開始搭乘</span
                    >
                  </div>
                  <div class="col-auto">
                    <span
                      class="fs-3 text-primary"
                      :title="detailData.GetonTime?.toString()">
                      {{ ValueOrDash(ProcessTimeFormat(detailData.GetonTime)) }}
                    </span>
                  </div>
                </div>
              </div>
              <div class="py-1 border-bottom border-bottom-dashed">
                <div class="row gx-2 align-items-center">
                  <div class="col">
                    <span
                      class="small text-spacing-4 fw-bolder text-label-color"
                      >下車時間</span
                    >
                  </div>
                  <div class="col-auto">
                    <span
                      class="fs-3 text-primary"
                      :title="detailData.GetoffTime?.toString()">
                      {{
                        ValueOrDash(ProcessTimeFormat(detailData.GetoffTime))
                      }}
                    </span>
                  </div>
                </div>
              </div>
              <div class="py-1">
                <div class="row gx-2 align-items-center">
                  <div class="col">
                    <span
                      class="small text-spacing-4 fw-bolder text-label-color"
                      >乘車時間</span
                    >
                  </div>
                  <div class="col-auto">
                    <span class="fs-3 text-primary">{{
                      ValueOrDash(
                        SecondToMinuteFormat(detailData.TravelTime ?? 0),
                      )
                    }}</span>
                    <span
                      class="small text-spacing-4 fw-bolder text-label-color"
                      >分鐘</span
                    >
                  </div>
                </div>
              </div>
            </div>
            <!-- 載客上車前拍照 -->
            <div
              v-if="detailData.PickupPictureUrl || !hidePickupPicture"
              class="col-auto ps-4 pe-2">
              <img
                class="rounded"
                :src="ImageUrlFormat(detailData.PickupPictureUrl)"
                width="114"
                @error="OnPickupPictureError" />
            </div>
          </div>
          <div class="py-1 border-bottom border-bottom-dashed">
            <div class="row gx-2 align-items-center text-center">
              <div class="col">
                <div class="small text-spacing-4 fw-bolder text-label-color"
                  >跳表金額</div
                >
                <div class="fs-2 text-primary fw-bolder">{{
                  ValueOrDash(detailData.MeterAmount)
                }}</div>
              </div>
              <div class="col">
                <div class="small text-spacing-4 fw-bolder text-label-color"
                  >使用優惠卷</div
                >
                <div class="fs-2 text-danger">
                  <span
                    :class="{
                      'text-negative-number': detailData.BounsAmount < 0,
                      'fw-bolder': true,
                    }"
                    >{{
                      detailData.BounsAmount == null
                        ? ValueOrDash()
                        : Math.abs(detailData.BounsAmount)
                    }}</span
                  >
                </div>
              </div>
              <div class="col">
                <div class="small text-spacing-4 fw-bolder text-label-color"
                  >實收金額</div
                >
                <div class="fs-2 text-primary fw-bolder">{{
                  ValueOrDash(detailData.ActualAmount)
                }}</div>
              </div>
            </div>
          </div>
          <div class="py-1 border-bottom border-bottom-dashed">
            <div class="row gx-2 align-items-center">
              <div class="col-auto">
                <span class="small text-spacing-4 fw-bolder text-label-color"
                  >乘客評價</span
                >
              </div>
              <div class="col">
                <Rating :value="detailData.PassengerRatingDriver"></Rating>
              </div>
            </div>
          </div>
          <div class="py-2 mb-3">
            <div class="small text-spacing-4 fw-bolder text-label-color mb-2"
              >備註</div
            >
            <div class="fs-5">{{ detailData.PassengerRatingRemark }}</div>
          </div>
        </div>
      </div>
    </div>

    <!-- 軌跡紀錄 -->
    <div class="card border-0 rounded-lg shadow-sm">
      <!-- 標題 -->
      <div
        class="card-header border-0 bg-primary-light text-center rounded-top-lg">
        <span class="text-spacing-5 text-primary fw-bolder">軌跡紀錄</span>
      </div>
      <!-- 底部GoogleMap -->
      <div class="card-footer border-0 p-0 overflow-hidden rounded-bottom-lg">
        <div id="trace-map" ref="traceMap" class="map"></div>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, onMounted, onUnmounted, ref, nextTick } from "vue";
import Icon from "@/components/Icon.vue";
import {
  CompleteChecked,
  Time,
  BoyFace,
  PhoneRounded,
  Circle,
  LocationFilled,
} from "@/utility/icon";
import LoadGoogleMap from "@/utility/load-map";
import { GetOrderDetail, GetOrderStatusTrace } from "@/api/driverApi/records";
import { useRoute } from "vue-router";
import { OrderRecordDetail } from "@/interface/order";
import { format, parseJSON } from "date-fns";
import {
  ImageSystemSize,
  PickUpPassengerPhotoUrlFormat,
} from "@/utility/image-path";
import Rating from "@/components/Rating.vue";
import { OrderEventStatus, OrderTraceRecord } from "@/interface/trace";
import GMapStyles from "@/config/google-map-styles.json";
import AlertMessage from "@/components/AlertMessage.vue";
import { ValueOrDash } from "@/utility/text";
import { DEFAULT_OPTIONS, GOOGLE_MAP_ICONS } from "@/config/mapConfig";

export default defineComponent({
  name: "OrderDetailView",
  components: {
    Icon,
    Rating,
    AlertMessage,
  },
  async setup() {
    const route = useRoute();
    const orderNo = ref<string>(
      route.params.orderno == null ? "" : (route.params.orderno as string),
    );
    const traceMap = ref<HTMLDivElement>();
    const map = ref<google.maps.Map>();
    const detailData = ref<OrderRecordDetail | null>(null);
    const hidePickupPicture = ref(false);

    const paths = ref<google.maps.LatLng[]>([]);
    const markers = ref<google.maps.Marker[]>([]);
    const polylines = ref<google.maps.Polyline[]>([]);
    const infos = ref<google.maps.InfoWindow[]>([]);

    const GetDetail = async () => {
      const { data } = await GetOrderDetail(orderNo.value);
      detailData.value = data;
    };

    const OrderEventStatusToString = (ev: OrderEventStatus) => {
      const record: { [x: number]: string } = {};

      record[OrderEventStatus.GOTO] = "前往";
      record[OrderEventStatus.ARRIVAL] = "到達";
      record[OrderEventStatus.GETON] = "載客";
      record[OrderEventStatus.GETOFF] = "下車";

      return record[ev] ?? "";
    };

    const RenderMap = (_map: google.maps.Map, trace: OrderTraceRecord) => {
      ClearMap();

      const path = trace.Path.map(
        (path) => new google.maps.LatLng(path.Latitude, path.Longitude),
      );

      // polyline
      const polyline = new google.maps.Polyline({
        path,
        strokeColor: "#2C7BE5",
        strokeOpacity: 0.7,
        strokeWeight: 4,
      });
      polyline.setMap(_map);

      // marker
      for (const event of trace.EventPoint) {
        const latlng = new google.maps.LatLng(event.Latitude, event.Longitude);
        const marker = new google.maps.Marker({
          position: latlng,
          icon: {
            path: google.maps.SymbolPath.CIRCLE,
            strokeColor: "#1F5AA6",
            strokeOpacity: 1,
            scale: 4,
          },
        });

        const info = new google.maps.InfoWindow({
          position: latlng,
          content: OrderEventStatusToString(event.Status),
        });

        marker.setMap(_map);
        info.open(_map, marker);
        marker.addListener("click", () => info.open(_map, marker));

        markers.value.push(marker);
        infos.value.push(info);
      }

      paths.value = path;
      polylines.value.push(polyline);

      nextTick(() => TryFitBounds());
    };

    const TryFitBounds = () => {
      if (!map.value) return;
      if (!paths.value.length) return;

      const bounds = new google.maps.LatLngBounds();

      for (const latlng of paths.value) {
        bounds.extend(latlng);
      }

      map.value.setCenter(bounds.getCenter());
      map.value.fitBounds(bounds);
    };

    const InitMap = async (el: HTMLElement) => {
      var _map = new google.maps.Map(el, {
        ...DEFAULT_OPTIONS,
        fullscreenControl: false,
        panControl: false,
        rotateControl: false,
        scaleControl: false,
        zoomControl: false,
        styles: GMapStyles,
      });

      const { data } = await GetOrderStatusTrace(orderNo.value);
      RenderMap(_map, data);

      map.value = _map;
    };

    const ClearMap = () => {
      if (!map.value) return;

      paths.value = [];
      infos.value.forEach((info) => info.close());
      infos.value = [];
      markers.value.forEach((marker) => marker.setMap(null));
      markers.value = [];
      polylines.value.forEach((polyline) => polyline.setMap(null));
      polylines.value = [];
    };

    const OnPickupPictureError = (event: Event) => {
      event.preventDefault();
      if (!hidePickupPicture.value) hidePickupPicture.value = true;
    };

    // format
    const OrderTimeFormat = (date: Date | string | null): string => {
      if (!date) {
        return "";
      }
      if (typeof date === "string") {
        date = parseJSON(date);
      }
      const pattern =
        date.getFullYear() !== new Date().getFullYear()
          ? "yyyy/MM/dd HH:mm"
          : "MM/dd HH:mm";
      return format(date, pattern);
    };
    const ProcessTimeFormat = (date: Date | string | null): string => {
      if (!date) {
        return "";
      }
      if (typeof date === "string") {
        date = parseJSON(date);
      }
      const pattern = "HH:mm";
      return format(date, pattern);
    };
    const ImageUrlFormat = (url: string | null) => {
      if (!url) {
        return "";
      }
      return PickUpPassengerPhotoUrlFormat(url, ImageSystemSize.Small);
    };
    const SecondToMinuteFormat = (seconds: number) => {
      return Math.round(seconds / 60);
    };

    onMounted(async () => {
      await LoadGoogleMap(process.env.VUE_APP_MAP_KEY ?? "");
      if (traceMap.value) {
        InitMap(traceMap.value);
      }
    });

    onUnmounted(() => {
      ClearMap();
    });

    await GetDetail();

    return {
      traceMap,
      detailData,
      hidePickupPicture,

      OnPickupPictureError,

      // methods
      OrderTimeFormat,
      ProcessTimeFormat,
      ImageUrlFormat,
      SecondToMinuteFormat,
      ValueOrDash,

      // icons
      BoyFace,
      Circle,
      CompleteChecked,
      LocationFilled,
      PhoneRounded,
      Time,
    };
  },
});
</script>
<style lang="scss" scoped>
.map {
  height: 30vh;
  min-height: 80px;
}
</style>
